/**
 * Who a downhole send goes to, before it goes (API-A, api#928) — shared by the
 * ticket send and the invoice send.
 *
 * THE PREVIEW IS THE SEND'S OWN RESOLVER (lib/downhole-recipients.ts on the
 * API), so what is shown is who the email will go to. Nothing here guesses a
 * recipient from the customer row: an invoice goes to accounting first, a
 * ticket to the main contact first, and only the API knows which applies.
 * A preview with no address (`to: null`) carries a code and a sentence — it is
 * shown as worded, and the send is held.
 *
 * CC is optional, at most ten, each a valid address; duplicates and a copy of
 * the `to` address are dropped by the API, which answers with what it actually
 * copied. It is checked here only so a typo is caught before the round trip;
 * the API's 400 CC_INVALID is still shown if it disagrees.
 */
import React from 'react';
import { Loader2 } from 'lucide-react';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import type { DhRecipient } from '../../types/downhole';
import { DH_INPUT, DhField } from './DownholeBits';

export const MAX_CC = 10;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The CC box's text as addresses: split on commas, semicolons and whitespace; blanks dropped. */
export function parseCcInput(text: string): { cc: string[]; problem: string | null } {
  const cc = text
    .split(/[\s,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);
  const bad = cc.filter((x) => !EMAIL_RE.test(x));
  if (bad.length) return { cc, problem: `${bad.join(', ')} ${bad.length === 1 ? 'is not an email address' : 'are not email addresses'}.` };
  if (cc.length > MAX_CC) return { cc, problem: `At most ${MAX_CC} addresses can be copied.` };
  return { cc, problem: null };
}

interface PreviewData {
  to: DhRecipient | null;
  message?: string;
  last_sent: { at: string; to: string | null; cc: string[] | null } | null;
}

/** "Goes to …", or the API's no-address sentence; and when it was last sent, to whom. */
export function RecipientPreview({
  query,
  tz,
  subject,
}: {
  query: { data?: { data: PreviewData | null }; isLoading: boolean; error?: unknown; fetchStatus?: string; failureReason?: unknown };
  tz: string | null;
  subject: string;
}) {
  const failure = queryLoadFailure(query);
  const p = query.data?.data ?? null;
  return (
    <div data-testid="recipient-preview" className="rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-4 py-3 text-sm text-fieldiq-text">
      {query.isLoading || (!failure && !query.data) ? (
        <span className="inline-flex items-center gap-2 text-fieldiq-muted">
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          Checking who this goes to…
        </span>
      ) : failure && !p ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject={subject} />
      ) : !p ? (
        <span>—</span>
      ) : p.to ? (
        <>
          <p>
            Goes to <span className="font-bold">{p.to.name ? `${p.to.name} <${p.to.email}>` : p.to.email}</span>
          </p>
          <p className="text-xs text-fieldiq-muted">{p.to.source_label}</p>
        </>
      ) : (
        <p role="alert" className="font-bold">
          {p.message || 'There is no address to send this to.'}
        </p>
      )}
      {p?.last_sent && (
        <p className="mt-2 border-t border-fieldiq-border pt-2 text-xs text-fieldiq-muted">
          Last sent {formatInstant(p.last_sent.at, tz) ?? '—'} to {p.last_sent.to || '—'}
          {p.last_sent.cc && p.last_sent.cc.length ? `, copied to ${p.last_sent.cc.join(', ')}` : ''}.
        </p>
      )}
    </div>
  );
}

export function CcField({ value, onChange, problem }: { value: string; onChange: (v: string) => void; problem: string | null }) {
  return (
    <div>
      <DhField label="CC" hint={`Optional. Up to ${MAX_CC} addresses, separated by commas.`}>
        <input
          className={DH_INPUT}
          type="text"
          inputMode="email"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem ? 'cc-problem' : undefined}
        />
      </DhField>
      {problem && (
        <p id="cc-problem" role="alert" className="mt-1 border-l-2 border-fi-signal-red bg-fi-signal-red/10 px-2 py-1 text-xs text-fieldiq-text">
          {problem}
        </p>
      )}
    </div>
  );
}
